import type { AutomationSuiteDto } from '@tms/shared';
import { useNavigate } from '@tanstack/react-router';
import { GitBranch, Layers, Play, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  SkeletonRows,
  Textarea,
} from '../../../components/ui';
import { useT } from '../../../i18n';
import { useAutomationCan, useCreateSuite, useDeleteSuite, useSuites } from '../hooks';
import { useFailToast } from './shared';

const AddSuiteDialog = ({
  projectId,
  onOpenChange,
}: {
  projectId: string;
  onOpenChange: (open: boolean) => void;
}) => {
  const { t } = useT();
  const failToast = useFailToast();
  const create = useCreateSuite(projectId);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const add = () =>
    create.mutate(
      { name: name.trim(), description },
      {
        onSuccess: () => {
          toast.success(t('automation.project.suites.added', { name: name.trim() }));
          onOpenChange(false);
        },
        onError: failToast,
      },
    );

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      title={t('automation.project.suites.dialogTitle')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!name.trim()}
            loading={create.isPending}
            onClick={add}
          >
            {t('automation.project.data.add')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label={t('automation.project.field.name')} required>
          {({ id }) => (
            <Input
              id={id}
              value={name}
              autoFocus
              placeholder={t('automation.project.suites.namePlaceholder')}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('common.description')}>
          {({ id }) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              placeholder={t('automation.project.suites.descriptionPlaceholder')}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
};

/**
 * Test suites: found by the scanner (repo) or added by hand (manual). Admins
 * add and delete them — only the ones added by hand; QA starts a suite.
 */
export const SuitesTab = ({ projectId }: { projectId: string }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const can = useAutomationCan();
  const failToast = useFailToast();
  const suites = useSuites(projectId);
  const remove = useDeleteSuite(projectId);
  const [addOpen, setAddOpen] = useState(false);
  const [deleting, setDeleting] = useState<AutomationSuiteDto | null>(null);

  if (suites.error && !suites.data) {
    return (
      <Card>
        <ErrorState error={suites.error} onRetry={() => void suites.refetch()} />
      </Card>
    );
  }
  if (!suites.data) return <SkeletonRows rows={3} className="p-0" />;

  // the run form opens with this suite chosen
  const startRun = (suite: string) =>
    void navigate({ to: '/automation/runs/new', search: { project: projectId, suite } });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="inline-flex items-center gap-1 text-[13px] text-ink-muted">
          <GitBranch className="size-3.5 shrink-0" aria-hidden="true" />
          {t('automation.project.suites.hint')}
        </p>
        {can.manage ? (
          <Button variant="primary" size="sm" onClick={() => setAddOpen(true)}>
            <Plus className="size-3.5" />
            {t('automation.project.suites.add')}
          </Button>
        ) : null}
      </div>

      {suites.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Layers className="size-6" />}
            title={t('automation.project.suites.empty')}
            description={t('automation.project.suites.emptyHint')}
            action={
              can.manage ? (
                <Button onClick={() => setAddOpen(true)}>
                  <Plus className="size-3.5" />
                  {t('automation.project.suites.addFirst')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {suites.data.map((suite) => (
            <Card key={suite.id} className="flex flex-col gap-3 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-9 shrink-0 place-items-center rounded-md bg-accent-soft text-accent-fg">
                    <Layers className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[14px] font-semibold text-ink">{suite.name}</p>
                    <p className="text-[12px] text-ink-subtle">
                      {t('automation.projectTests', { count: suite.testCount })}
                    </p>
                  </div>
                </div>
                <Badge
                  className={suite.source === 'repo' ? 'bg-accent-soft text-accent-fg' : undefined}
                >
                  {suite.source === 'repo'
                    ? t('automation.project.suites.sourceRepo')
                    : t('automation.project.suites.sourceManual')}
                </Badge>
              </div>
              <p className="min-h-9 text-[13px] text-ink-muted">{suite.description}</p>
              {can.run || (can.manage && suite.source === 'manual') ? (
                <div className="mt-auto flex gap-2">
                  {can.run ? (
                    <Button size="sm" className="flex-1" onClick={() => startRun(suite.name)}>
                      <Play className="size-3.5" />
                      {t('automation.project.run')}
                    </Button>
                  ) : null}
                  {can.manage && suite.source === 'manual' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-fail"
                      aria-label={t('automation.project.suites.delete', { name: suite.name })}
                      onClick={() => setDeleting(suite)}
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}

      {addOpen ? <AddSuiteDialog projectId={projectId} onOpenChange={setAddOpen} /> : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={t('automation.project.suites.deleteTitle')}
        description={t('automation.project.suites.deleteDescription', {
          name: deleting?.name ?? '',
        })}
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, {
            onSuccess: () => {
              toast.success(t('automation.project.suites.deleted'));
              setDeleting(null);
            },
            onError: failToast,
          })
        }
      />
    </div>
  );
};
